import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getIssues, setIssuesList } from '../store/slices/issueSlice';
import { extractList } from '../utils/response';
import { getUsersList } from '../store/slices/userSlice';
import { getLeadsStatus } from '../store/slices/leadsSlice';
import { getErrorMessage } from '../utils/errorHandler';

const buildQueryParams = (page, pageSize, search, filters, sort) => {
  const params = {
    'relations[]': ['author', 'lastIssueLog', 'issueLogs', 'model', 'lead'],
    paginate: 1,
    page: page + 1,
    per_page: pageSize,
    ...(search && { search }),
  };
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value && value !== '') params[`filters[${key}]`] = value;
  });
  if (sort?.sort_by) params.sort_by = sort.sort_by;
  if (sort?.sort_direction) params.sort_direction = sort.sort_direction;
  return params;
};

const mapUserOption = (u) => ({
  id: u?.uuid ?? u?.id,
  name: u?.name || u?.username || u?.email || 'Unnamed',
});

export const useIssues = (canViewListIssue = true) => {
  const dispatch = useDispatch();
  const issuesState = useSelector((state) => state.issues);

  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    title: '',
    status_id: '',
    author_id: '',
    model_type: '',
  });
  const [sort, setSort] = useState({
    sort_by: 'created_at',
    sort_direction: 'desc',
  });
  const [processingId, setProcessingId] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [statusFilterLoading, setStatusFilterLoading] = useState(false);
  const [usersFilterLoading, setUsersFilterLoading] = useState(false);
  const statusInFlightRef = useRef(null);
  const usersInFlightRef = useRef(null);

  const issuesResponse = issuesState.issues;
  const issues = extractList(issuesResponse);
  const tableLoading = issuesState.loading;

  const fetchIssues = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize, search = searchValue) => {
      if (!canViewListIssue) {
        toast.error('You do not have permission to view issues.');
        return;
      }
      const params = buildQueryParams(page, pageSize, search, filters, sort);
      try {
        const res = await dispatch(getIssues(params)).unwrap();
        if (res?.success && res?.data?.pagination) {
          setPagination((p) => ({ ...p, total: res.data.pagination?.total || 0 }));
        }
      } catch (e) {
        toast.error('Failed to fetch issues');
      }
    },
    [
      canViewListIssue,
      dispatch,
      filters,
      sort,
      pagination.page,
      pagination.pageSize,
      searchValue,
    ]
  );

  const fetchIssuesRef = useRef(fetchIssues);
  fetchIssuesRef.current = fetchIssues;
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const ensureStatusFilterOptions = useCallback(async () => {
    if (statusOptions.length > 0) return statusOptions;
    if (statusInFlightRef.current) return statusInFlightRef.current;
    setStatusFilterLoading(true);
    const request = (async () => {
      try {
        const data = await dispatch(getLeadsStatus({ 'conditions[model]': 'Issue' })).unwrap();
        const list = extractList(data);
        setStatusOptions(list);
        return list;
      } catch (e) {
        toast.error(getErrorMessage(e) || 'Failed to load statuses');
        return [];
      } finally {
        setStatusFilterLoading(false);
        statusInFlightRef.current = null;
      }
    })();
    statusInFlightRef.current = request;
    return request;
  }, [dispatch, statusOptions]);

  const ensureUsersFilterOptions = useCallback(async () => {
    if (usersList.length > 0) return usersList;
    if (usersInFlightRef.current) return usersInFlightRef.current;
    setUsersFilterLoading(true);
    const request = (async () => {
      try {
        const data = await dispatch(getUsersList({ paginate: 0 })).unwrap();
        const list = extractList(data).map(mapUserOption).filter((u) => u.id != null);
        setUsersList(list);
        return list;
      } catch (e) {
        toast.error(getErrorMessage(e) || 'Failed to load users');
        return [];
      } finally {
        setUsersFilterLoading(false);
        usersInFlightRef.current = null;
      }
    })();
    usersInFlightRef.current = request;
    return request;
  }, [dispatch, usersList]);

  useEffect(() => {
    if (!canViewListIssue) return;
    fetchIssues();
  }, [canViewListIssue, pagination.page, pagination.pageSize, filters, sort, fetchIssues]);

  useEffect(() => {
    if (!canViewListIssue) return;
    const timer = setTimeout(() => {
      setPagination((p) => ({ ...p, page: 0 }));
      const { pageSize } = paginationRef.current;
      fetchIssuesRef.current(0, pageSize, searchValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchValue, canViewListIssue]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSortChange = (fieldOrSort) => {
    if (fieldOrSort && typeof fieldOrSort === 'object' && !Array.isArray(fieldOrSort)) {
      setSort({
        sort_by: fieldOrSort.sort_by ?? 'created_at',
        sort_direction: fieldOrSort.sort_direction ?? 'desc',
      });
      return;
    }
    setSort((prev) => ({
      sort_by: fieldOrSort,
      sort_direction:
        prev.sort_by === fieldOrSort && prev.sort_direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const clearFilters = () => {
    setFilters({ title: '', status_id: '', author_id: '', model_type: '' });
    setSort({ sort_by: 'created_at', sort_direction: 'desc' });
  };

  const handleSearchChange = (e) => setSearchValue(e.target.value);

  const handleClearSearch = () => {
    setSearchValue('');
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const handlePageChange = (_, newPage) =>
    setPagination((p) => ({ ...p, page: newPage }));

  const handleRowsPerPageChange = (e) => {
    const sz = parseInt(e.target.value, 10);
    setPagination((p) => ({ ...p, page: 0, pageSize: sz }));
  };

  const setIssues = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setIssuesList(updater));
    }
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(
      (v) => v !== '' && v !== null && v !== undefined
    ).length;
  };

  return {
    issues,
    tableLoading,
    searchValue,
    pagination,
    filters,
    sort,
    usersList,
    statusOptions,
    statusFilterLoading,
    usersFilterLoading,
    processingId,
    setProcessingId,
    setIssues,
    fetchIssues,
    ensureStatusFilterOptions,
    ensureUsersFilterOptions,
    handleSearchChange,
    handleClearSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleSortChange,
    clearFilters,
    getActiveFiltersCount,
  };
};
